import { useCallback, useEffect, useMemo, useState } from 'react';
import { CategoryEntry } from '../types';
import { getFullyExpandedIds } from '../../tree/utils';

export interface UseCategoryNavigation {
  navigationPath: string[];
  onNavigate: (item: CategoryEntry, level: number) => void;
}

export interface UseCategoryNavigationOptions {
  items: CategoryEntry[];
  multiple?: boolean;
  defaultExpandedKeys?: string[];
}

const INITIAL_NAVIGATION_PATH = [''];

export function useCategoryNavigation({
  items,
  multiple,
  defaultExpandedKeys,
}: UseCategoryNavigationOptions): UseCategoryNavigation {
  const [userPath, setUserPath] = useState<string[]>(INITIAL_NAVIGATION_PATH);
  const [hasUserNavigated, setHasUserNavigated] = useState(false);
  const initialExpandedPath = useMemo(
    () => (defaultExpandedKeys != null ? ['', ...getFullyExpandedIds(items, defaultExpandedKeys).reverse()] : null),
    [defaultExpandedKeys, items],
  );

  const effectivePath = useMemo(() => {
    if (hasUserNavigated) return userPath;
    if (initialExpandedPath && !multiple) return initialExpandedPath;
    return INITIAL_NAVIGATION_PATH;
  }, [hasUserNavigated, userPath, initialExpandedPath, multiple]);

  const handleNavigate = useCallback(
    (item: CategoryEntry, level: number) => {
      setHasUserNavigated(true);
      setUserPath([...effectivePath.slice(0, level + 1), item.id]);
    },
    [effectivePath],
  );

  useEffect(() => {
    setHasUserNavigated(false);
    setUserPath(INITIAL_NAVIGATION_PATH);
  }, [items]);

  return {
    navigationPath: effectivePath,
    onNavigate: handleNavigate,
  };
}
